// Libraries
import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Styles
import styles from './FormBuilderPage.module.css';

// Components
import FormBuilderTool from '../FormBuilderTool';
import Preview from '../Preview/Preview';
import Mapping from '../Mapping/Mapping';
import Settings from '../Settings/Settings';

import TabPanel from 'src/components/UI/appbar/TabPanel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import axios from 'src/axios';
import { Button, Chip, IconButton, Tooltip } from '@mui/material';

// Icons
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';

import { EditMode, FormData } from '../../CreateQuestionnaire';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const TAB_ICONS = [
  <EditNoteRoundedIcon fontSize="small" key="edit" />,
  <VisibilityOutlinedIcon fontSize="small" key="preview" />,
  <AccountTreeOutlinedIcon fontSize="small" key="mapping" />,
  <RuleRoundedIcon fontSize="small" key="settings" />
];

function FormBuilderPage() {
  const formData = useContext(FormData);
  const edit = useContext(EditMode);
  const navigate = useNavigate();
  const { setSnack } = React.useContext(SnackContext);
  const mappingsRef = useRef(null);
  const formTypeRef = useRef('consent');

  const saveConfig = () => {
    axios
      .post(APICONSTANTS.formBuilderSchemas, { formData: formData[0] })
      .then((res) => {
        setSnack({
          status: true,
          msg: 'Questionnaire created',
          severity: 'success'
        });
        if (res.data.success) navigate(CONSTANTS.formCreationPath);
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'An error has occurred!',
          severity: 'error'
        });
      });
  };

  const updateConfig = async (id) => {
    if (mappingsRef.current !== null && edit.schemaId) {
      try {
        await axios.post(
          APICONSTANTS.formBuilderSchemasMappings.replace(
            ':_id',
            edit.schemaId
          ),
          { [formTypeRef.current || 'consent']: mappingsRef.current }
        );
      } catch {
        // Mapping save failure is non-fatal; proceed with form update
      }
    }

    axios
      .put(APICONSTANTS.formBuilderSchemas + '/' + id, {
        formData: formData[0]
      })
      .then((res) => {
        setSnack({
          status: true,
          msg: 'Questionnaire updated successfully',
          severity: 'success'
        });
        if (res.data.success) navigate(CONSTANTS.formCreationPath);
      })
      .catch((err) => {
        if (err.response.status === 403) {
          setSnack({
            status: true,
            msg: 'Unauthorized !',
            severity: 'error'
          });
        } else {
          setSnack({
            status: true,
            msg: 'An error has occurred!',
            severity: 'error'
          });
        }
      });
  };
  const TAB_PANEL_COMPONENTS = [
    {
      label: 'Edit',
      component: <FormBuilderTool />
    },
    {
      label: 'Preview',
      component: <Preview />
    },
    {
      label: 'Mapping',
      component: <Mapping mappingsRef={mappingsRef} formTypeRef={formTypeRef} />
    },
    {
      label: 'Post Submission Action',
      component: <Settings />
    }
  ];

  const [value, setValue] = useState(0);

  function handleChange(event, newValue) {
    setValue(newValue);
  }

  const t = useAutoTranslation();

  const form = formData[0] || {};

  return (
    <div className={styles.Page}>
      {/* Toolbar */}
      <div className={styles.Toolbar}>
        <div className={styles.ToolbarLeft}>
          <Tooltip title={t('Back to forms')} arrow>
            <IconButton
              size="small"
              className={styles.BackBtn}
              onClick={() => navigate(CONSTANTS.formCreationPath)}
            >
              <ArrowBackRoundedIcon />
            </IconButton>
          </Tooltip>
          <div className={styles.TitleBlock}>
            <div className={styles.TitleRow}>
              <h1 className={styles.FormTitle}>
                {form.title || t('Create New Form')}
              </h1>
              <Chip
                size="small"
                label={edit.status ? t('Editing') : t('Draft')}
                className={edit.status ? styles.EditingChip : styles.DraftChip}
              />
            </div>
            <p className={styles.FormSubtitle}>
              {form.description || t('Build custom forms and questionnaires')}
            </p>
          </div>
        </div>
        <div className={styles.ToolbarRight}>
          {edit.status ? (
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              startIcon={<SaveOutlinedIcon />}
              // edit.schemaId not equal to schemaId from formData schema
              onClick={() => updateConfig(edit.schemaId)}
            >
              {t(`Update`)}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              startIcon={<SaveOutlinedIcon />}
              onClick={saveConfig}
            >
              {t(`Save`)}
            </Button>
          )}
        </div>
      </div>
      {/* Tab navigation */}
      <div className={styles.TabsRow}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className={styles.Tabs}
          slotProps={{
            indicator: { className: styles.TabIndicator }
          }}
        >
          {TAB_PANEL_COMPONENTS.map((el, index) => (
            <Tab
              key={index}
              icon={TAB_ICONS[index]}
              iconPosition="start"
              label={t(el.label)}
              disableRipple
              className={styles.Tab}
            />
          ))}
        </Tabs>
      </div>
      {/* Contextual tip — only relevant while editing the structure */}
      {/* {value === 0 && (
        <div className={styles.Tip}>
          <InfoOutlinedIcon fontSize="small" className={styles.TipIcon} />
          <span>
            {t(`Add form elements to a section to group them together.`)}{' '}
            {t(
              `Conditions can be added to sections to make them visible only when conditions are met.`
            )}
          </span>
        </div>
      )} */}
      <TabPanel value={value} index={value}>
        <div className={styles.FormBuilderContainer}>
          {TAB_PANEL_COMPONENTS[value].component}
        </div>
      </TabPanel>
    </div>
  );
}

export default FormBuilderPage;
